/**
 * W1-11a (app-registry-keymap): key labels in the user's keyboard layout (DESIGN.md §8.1).
 *
 * Bindings use physical codes (KeyboardEvent.code); labels show what the key prints on the user's layout:
 *   1. `navigator.keyboard.getLayoutMap()` (Chromium): exact, e.g. KeyQ -> "A" on AZERTY;
 *   2. characters learned from real key presses (unmodified, unshifted keydowns), so Firefox users see their
 *      layout after typing a key once;
 *   3. otherwise the US label, flagged `physical` so the keymap can append "(physical)".
 * Digit-row keys always show their digit (keycaps print it even where the unshifted character differs, e.g. "&" on
 * AZERTY). The layout map is re-read when the window regains focus (the user may have switched layouts).
 */

import type { Unsubscribe } from '../../core/util/types';
import { altGraph, codePointLength, LAYOUT_CODES } from './chords';

export interface KeyLabel {
  readonly text: string;
  /** True when the label is the US fallback (the user's layout is unknown for this key). */
  readonly physical: boolean;
}

/** Unshifted US characters of every layout-dependent code. */
export const US_LAYOUT: Readonly<Record<string, string>> = {
  ...Object.fromEntries('abcdefghijklmnopqrstuvwxyz'.split('').map((c) => [`Key${c.toUpperCase()}`, c])),
  ...Object.fromEntries('0123456789'.split('').map((d) => [`Digit${d}`, d])),
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Backquote: '`',
  Comma: ',',
  Period: '.',
  Slash: '/',
  IntlBackslash: '\\',
  IntlRo: '\\',
  IntlYen: '\\',
};

interface LayoutMapLike {
  get(code: string): string | undefined;
  forEach?(cb: (value: string, key: string) => void): void;
}

interface KeyboardLike {
  getLayoutMap(): Promise<LayoutMapLike>;
}

export interface LayoutLabels {
  /** Label of a layout-dependent code (letters, digits, punctuation); null for other codes. */
  labelFor(code: string): KeyLabel | null;
  /** Learn the unshifted character of a key from a real keydown (Firefox fallback). */
  learn(
    e: Pick<KeyboardEvent, 'code' | 'key' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey'> & {
      getModifierState?: (k: string) => boolean;
    },
  ): void;
  /** (Re)load the layout map; resolves when done (immediately without the Keyboard API). */
  refresh(): Promise<void>;
  /** 'layoutMap' once the Keyboard API answered, else 'fallback'. */
  readonly source: 'layoutMap' | 'fallback';
  subscribe(cb: () => void): Unsubscribe;
  dispose(): void;
}

export interface LayoutLabelsOptions {
  /** The Keyboard API (default `navigator.keyboard`); null = unavailable (Firefox, Safari). */
  readonly keyboard?: KeyboardLike | null;
  /** Window to watch for focus (re-read the map); null = none. */
  readonly window?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null;
}

function defaultKeyboard(): KeyboardLike | null {
  if (typeof navigator === 'undefined') return null;
  const kb = (navigator as Navigator & { keyboard?: Partial<KeyboardLike> }).keyboard;
  return kb && typeof kb.getLayoutMap === 'function' ? (kb as KeyboardLike) : null;
}

function display(code: string, ch: string): string {
  if (code.startsWith('Digit')) return code.slice(5);
  return ch.length === 1 ? ch.toUpperCase() : ch;
}

export function createLayoutLabels(opts: LayoutLabelsOptions = {}): LayoutLabels {
  const keyboard = opts.keyboard === undefined ? defaultKeyboard() : opts.keyboard;
  const win = opts.window === undefined ? (typeof window === 'undefined' ? null : window) : opts.window;
  const listeners = new Set<() => void>();
  const learned = new Map<string, string>();
  let map: Map<string, string> | null = null;
  let source: 'layoutMap' | 'fallback' = 'fallback';
  let loading: Promise<void> | null = null;
  let started = false;

  const notify = (): void => {
    for (const cb of [...listeners]) cb();
  };

  const load = (): Promise<void> => {
    if (keyboard === null) return Promise.resolve();
    loading ??= keyboard
      .getLayoutMap()
      .then((layout) => {
        const next = new Map<string, string>();
        for (const code of LAYOUT_CODES) {
          const ch = layout.get(code);
          if (ch !== undefined && ch !== '') next.set(code, ch);
        }
        const changed = map === null || next.size !== map.size || [...next].some(([code, ch]) => map?.get(code) !== ch);
        map = next;
        source = 'layoutMap';
        if (changed) notify();
      })
      .catch(() => {
        // Insecure context, iframe or permission policy: keep the fallback labels.
      })
      .finally(() => {
        loading = null;
      });
    return loading;
  };

  const onFocus = (): void => {
    void load();
  };

  const start = (): void => {
    if (started) return;
    started = true;
    void load();
    win?.addEventListener('focus', onFocus);
  };

  return {
    labelFor(code) {
      if (!LAYOUT_CODES.has(code)) return null;
      start();
      const fromMap = map?.get(code);
      if (fromMap !== undefined) return { text: display(code, fromMap), physical: false };
      const fromKey = learned.get(code);
      if (fromKey !== undefined) return { text: display(code, fromKey), physical: false };
      const us = US_LAYOUT[code];
      if (us === undefined) return null;
      // Digit keys print their digit on every layout; other keys are only known for the US layout.
      return { text: display(code, us), physical: map === null && !code.startsWith('Digit') };
    },
    learn(e) {
      if (map !== null || e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
      if (!LAYOUT_CODES.has(e.code) || codePointLength(e.key) !== 1 || e.key.trim() === '' || altGraph(e)) return;
      const ch = e.key.toLowerCase();
      if (learned.get(e.code) === ch) return;
      learned.set(e.code, ch);
      notify();
    },
    refresh() {
      started = true;
      return load();
    },
    get source() {
      return source;
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    dispose() {
      win?.removeEventListener('focus', onFocus);
      listeners.clear();
    },
  };
}

/** App-wide labels (loads the layout map lazily on first use). */
export const layoutLabels: LayoutLabels = createLayoutLabels();
